import {
    BarChart,
    Copy,
    Download,
    Edit,
    ExternalLink,
    MoreHorizontal,
    QrCode,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useNavigate } from 'react-router-dom';
import { getAllUrls, updateUrl } from '../api/urlService';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.tsx';
import { Input } from '../components/ui/input.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table.tsx';

interface Url {
  id: string;
  shortCode: string;
  originalUrl: string;
  shortUrl: string;
  qrCodeUrl: string;
  visits: number;
  createdAt: string;
}

const UrlList = () => {
  const navigate = useNavigate();
  const [urls, setUrls] = useState<Url[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<Url | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [urlToEdit, setUrlToEdit] = useState<Url | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        setLoading(true);
        const data = await getAllUrls();
        setUrls(data);
      } catch (err) {
        setError('Failed to fetch URLs');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUrls();

    // Set up interval to refresh URL data every 30 seconds
    const intervalId = setInterval(() => {
      fetchUrls();
    }, 30000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleCopy = (id: string) => {
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const viewStats = (shortCode: string) => {
    navigate(`/urls/${shortCode}`);
  };

  const openQrDialog = (url: Url) => {
    setSelectedUrl(url);
    setQrDialogOpen(true);
  };

  const closeQrDialog = () => {
    setQrDialogOpen(false);
  };

  const downloadQRCode = () => {
    if (!selectedUrl) return;

    const link = document.createElement('a');
    link.href = selectedUrl.qrCodeUrl;
    link.download = `qrcode-${selectedUrl.shortCode}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openEditDialog = (url: Url) => {
    setUrlToEdit(url);
    setNewUrl(url.originalUrl);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setUrlToEdit(null);
    setNewUrl('');
  };

  const handleUpdateUrl = async () => {
    if (!urlToEdit || !newUrl) return;

    try {
      setUpdating(true);
      const response = await updateUrl(urlToEdit.shortCode, newUrl);

      setUrls(urls.map(url => url.id === urlToEdit.id ? response.url : url));
      setNotification({
        show: true,
        message: 'URL updated successfully',
        type: 'success'
      });
      closeEditDialog();
    } catch (error: any) {
      setNotification({
        show: true,
        message: error.error || 'Failed to update URL',
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const truncateUrl = (url: string, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">All Shortened URLs & QR Codes</h1>
          <p className="text-muted-foreground">
            View all your shortened URLs, QR codes, and their visit statistics.
          </p>
        </div>

        {urls.length === 0 ? (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>No URLs found</CardTitle>
              <p className="text-muted-foreground">
                Create your first shortened URL to see it here.
              </p>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button onClick={() => navigate('/')}>
                Create URL
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Short URL</TableHead>
                  <TableHead>Original URL</TableHead>
                  <TableHead className="text-center">Visits</TableHead>
                  <TableHead className="text-center">Created</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {urls.map((url) => (
                  <TableRow key={url.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <a
                          href={`/${url.shortCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline flex items-center gap-1"
                        >
                          {url.shortCode}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        <CopyToClipboard text={url.shortUrl} onCopy={() => handleCopy(url.id)}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={copiedId === url.id ? "text-green-500" : ""}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </CopyToClipboard>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate">
                      <span title={url.originalUrl}>
                        {truncateUrl(url.originalUrl)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">{url.visits}</TableCell>
                    <TableCell className="text-center">{formatDate(url.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewStats(url.shortCode)}>
                              <BarChart className="mr-2 h-4 w-4" />
                              View Stats
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openQrDialog(url)}>
                              <QrCode className="mr-2 h-4 w-4" />
                              View QR Code
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(url)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit URL
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>
              Scan this QR code to access the shortened URL
            </DialogDescription>
          </DialogHeader>
          {selectedUrl && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={selectedUrl.qrCodeUrl}
                alt="QR Code"
                className="w-48 h-48"
              />
              <Button onClick={downloadQRCode}>
                <Download className="mr-2 h-4 w-4" />
                Download QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit URL Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit URL</DialogTitle>
            <DialogDescription>
              Update the destination URL for this shortened link
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="Enter new URL"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEditDialog}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUrl}
              disabled={updating}
            >
              {updating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification */}
      {notification.show && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-destructive'
        } text-white`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default UrlList;
